import {useField} from 'formik';
import _ from 'lodash';
import React, {useCallback, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import {withTranslation} from 'react-i18next';
import styled from 'styled-components';

import {getFileUploadUrl, uploadToBlobStorage} from '../../../ducks/data';
import {unwrapNullableDataType} from '../../../ducks/formattersettingshelpers';
import {Icon} from '../../../ui-toolkit/elements/Icon/Icon';
import {Badge} from '../../../ui-toolkit/elements/badge/Badge';
import {basicBtnSetup} from '../../../ui-toolkit/elements/button/btn-setup/btnStyling';
import {Btn} from '../../../ui-toolkit/elements/button/btn-type/Btn';
import {Title, TitleContainer} from '../../../ui-toolkit/elements/txt/Txt';
import {getAcceptV11} from './dropzone-helpers';

function FileDropzone({entity, entityType, parentTypeId, fieldName, accepted, t}) {
    const fileDataType = unwrapNullableDataType(entityType);
    const multipleFiles = _.get(fileDataType, 'discriminatorValue') === 'ArrayDataType';
    const initialValue = _.isArray(entity) ? entity : _.isObject(entity) ? [entity] : [];
    const [uploadedFiles, setUploadedFiles] = useState(initialValue);
    const [, , fieldHelper] = useField(fieldName);

    const addFiles = (files) => {
        if (multipleFiles) {
            setUploadedFiles(_.concat(uploadedFiles, files));
            fieldHelper.setValue(_.concat(uploadedFiles, files));
        } else {
            if (files.length > 0) {
                setUploadedFiles([_.first(files)]);
                fieldHelper.setValue(_.first(files));
            } else {
                setUploadedFiles([]);
                fieldHelper.setValue(null);
            }
        }
    };

    const removeFile = (file) => {
        let updatedList = _.filter(uploadedFiles, (x) => x != file);
        setUploadedFiles(updatedList);

        if (multipleFiles) fieldHelper.setValue(updatedList);
        else fieldHelper.setValue(null);
    };

    const doUpload = async (file, uploadUrl) => {
        return new Promise((resolve, reject) => {
            let reader = new FileReader();
            reader.onload = () => {
                uploadToBlobStorage(uploadUrl, reader.result, file.type)
                    .then((res) => resolve(res))
                    .catch((res) => reject(res));
            };

            reader.readAsArrayBuffer(file);
        });
    };

    const onDrop = useCallback((acceptedFiles) => {
        Promise.all(
            _.map(acceptedFiles, (file) =>
                getFileUploadUrl(file.name, parentTypeId).then((res) => ({file: file, upload: res.data})),
            ),
        ).then((uploads) => {
            Promise.all(
                _.map(uploads, ({file, upload}) => doUpload(file, upload.uploadUrl, upload.filePath, upload.fileUrl)),
            ).then(() => {
                let uploadedFiles = _.map(uploads, ({file, upload}) => ({
                    fileName: file.name,
                    fileUrl: upload.fileUrl,
                }));
                addFiles(uploadedFiles);
            });
        });
    });

    const allowedMimeTypes = accepted
        ? accepted
        : multipleFiles
        ? getAcceptV11(_.get(fileDataType, 'childDataType.allowedExtensions', '*'))
        : getAcceptV11(_.get(fileDataType, 'allowedExtensions', '*'));

    const {getRootProps, getInputProps} = useDropzone({
        onDrop: onDrop,
        accept: allowedMimeTypes,
        maxFiles: undefined,
        multiple: multipleFiles,
    });

    return (
        <>
            {(uploadedFiles.length == 0 || multipleFiles) && (
                <UploadBtnWrapper {...getRootProps()}>
                    <input {...getInputProps()} />
                    {multipleFiles ? (
                        <Badge outline color="primary">
                            <Icon name="uploadFile" /> {t('fileFormatter.dropzoneTextMultiple')}
                        </Badge>
                    ) : (
                        <Badge outline color="primary">
                            <Icon name="uploadFile" /> {t('fileFormatter.dropzoneTextSingle')}
                        </Badge>
                    )}
                </UploadBtnWrapper>
            )}
            <>
                {uploadedFiles.map((file, i) => (
                    <Wrapper key={i}>
                        <Title sub>{file.fileName}</Title>
                        <Btn link color="danger" type="button" onClick={() => removeFile(file)}>
                            <Icon name="close" />
                        </Btn>
                    </Wrapper>
                ))}
            </>
        </>
    );
}

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    background: var(--container-background-color-100);
    border: 1px solid var(--container-border-color-100);
    display: inline-flex;
    padding: var(--space-xsm) var(--space);
    border-radius: 3px;

    ${TitleContainer} {
        color: var(--default-text-color);
        margin-right: 4px;
    }
`;

const UploadBtnWrapper = styled.div`
    ${Badge.Container} {
        ${basicBtnSetup};
        padding: 4px 12px;
        cursor: pointer;
        &:hover {
            color: var(--primary-dark) !important;
            border-color: var(--primary-dark) !important;
        }
    }
`;

export default withTranslation()(FileDropzone);
